import { Fragment, useMemo } from "react";
import {
  PersonalizedPlanDetailsDescription,
  PersonalizedPlanDetailsType,
} from "@covertree/new-business-forms";

import { slugify } from "@covertree/utils";
import { Box, Theme, Typography, useMediaQuery } from "@mui/material";

import { CustomAccordion, Tooltip } from "@covertree/ui";
import { Coverage } from "@covertree/new-business-mappings";

interface MobileDetailsModalBodyProps {
  personalizedPlans: PersonalizedPlanDetailsType[];
  coveragesData: Coverage[];
}

export function MobileDetailsModalBody({
  personalizedPlans,
  coveragesData,
}: MobileDetailsModalBodyProps) {
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("xs")
  );

  const data = useMemo(() => {
    return coveragesData.map((coverage) => ({
      ...coverage,
      rowData: personalizedPlans.map((personalizedPlan) => {
        return {
          name: "Default name",
          ...personalizedPlan[coverage.accessor],
          title: personalizedPlan.title,
          isRecommended: personalizedPlan.isRecommended,
        };
      }),
    }));
  }, [coveragesData, personalizedPlans]);

  return (
    <Box
      sx={(theme) => ({
        paddingBottom: { xs: theme.spacing(4), sm: theme.spacing(1) },
      })}>
      {Object.values(data).map((rowBody) => {
        return (
          <Fragment key={rowBody.getName()}>
            <Box
              sx={(theme) => ({
                marginTop: theme.spacing(4),
                borderBottom: `1px solid ${theme.colors.grey3}`,
              })}>
              <CustomAccordion
                contentSx={(theme) => ({
                  marginTop: 0,
                  paddingLeft: 0,
                  color: theme.colors.grey1,
                })}
                content={
                  <Typography variant="body2" sx={{ fontSize: 16 }}>
                    {rowBody.getDescription()}
                  </Typography>
                }
                header={(isExpanded: boolean) => (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      mb: 1,
                    }}>
                    <img
                      src={rowBody.image}
                      alt={rowBody.getName()}
                      style={{ width: "90px" }}
                    />
                    <Typography
                      color={isExpanded ? "secondary" : "inherit"}
                      sx={(theme) => ({
                        marginLeft: theme.spacing(1),
                        fontSize: 18,
                      })}>
                      {rowBody.getName()}
                    </Typography>
                  </Box>
                )}
              />
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-evenly" }}>
              {rowBody.rowData.map(
                (
                  row: PersonalizedPlanDetailsDescription & {
                    title: string;
                    isRecommended: boolean;
                  },
                  index: number
                ) => (
                  <Box
                    key={row.title}
                    sx={(theme) => ({
                      width: "33%",
                      padding: theme.spacing(1),
                      paddingTop: theme.spacing(2),
                      ...(row.isRecommended && {
                        backgroundColor: `${theme.palette.info.main}0D`,
                      }),
                    })}>
                    <>
                      <Typography
                        variant="subtitle1"
                        sx={(theme) => ({
                          fontSize: {
                            xs: 14,
                            sm: 16,
                          },
                          color: theme.colors.grey1,
                        })}>
                        {row.title}
                      </Typography>
                      <Box
                        display="flex"
                        flexDirection="row"
                        flexWrap="wrap"
                        mt={2}>
                        <Typography
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            flexWrap: "wrap",
                            gap: 0.5,
                            fontSize: {
                              xs: 16,
                              sm: 20,
                            },
                          }}>
                          {row.value}
                          {row.tooltip && (
                            <Box
                              sx={{
                                position: "relative",
                                display: "flex",
                                alignItems: "center",
                              }}>
                              <Tooltip
                                dataTestId={`${slugify(rowBody!.getName())}_${index}`}
                                tooltip={row.tooltip}
                                size={isMobile ? "small" : "medium"}
                              />
                            </Box>
                          )}
                        </Typography>
                      </Box>
                      {row.description && (
                        <Typography
                          variant="subtitle1"
                          sx={(theme) => ({
                            color: theme.colors.grey1,
                            mt: 0.5,
                            fontSize: {
                              xs: 14,
                              sm: 16,
                            },
                          })}>
                          {row.description}
                        </Typography>
                      )}
                    </>
                  </Box>
                )
              )}
            </Box>
          </Fragment>
        );
      })}
    </Box>
  );
}
